<script setup>
import {computed, onMounted} from 'vue'
import {useRouter} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useModalsStore} from '@/stores/modals'
import {useCategoriesStore} from '@/stores/categories'
import {useFormStore} from '@/stores/form'
import FormControlCategoryTitle from '@/common/form-control/FormControlCategoryTitle'
import ItemStatusIcons from '@/common/ItemStatusIcons'
import vUserAccess from '@/vue/directives/userAccess'
import Button from '@/common/Button.vue'

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
    restrictedIds: Array,
    selectLimit: Number
})

const router = useRouter()

// Pinia Stores
const componentsStore = useComponentsStore()
const modalsStore = useModalsStore()
const categoriesStore = useCategoriesStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// Computed properties
const components = computed(() => {
    return componentsStore.items
})

const targetTableName = computed(() => {
    return props.field?.tableName || components.value.find(o => o.id === props.field.categoryComponentId)?.tableName
})

const tableName = computed(() => {
    let currentTableName = formStore.value?.tableName

    // If this is the content component then this field may belong to a CT component, in which case the
    // categories request must target the component_<id> table and not "content". The ID of the table's record
    // is assigned by FormControlContentContent to the field's contentComponentsItemId property.
    if (currentTableName === 'content') {
        let componentId = props.field.modulecomponentsID || props.field.modulecomponentsId
        let component = componentsStore.items.find(obj => obj.id === componentId)
        if (component) {
            currentTableName = component.tableName
        }
    }

    return currentTableName
})

const id = computed(() => {
    let currentId = formStore.value?.id

    // If this is the content component then this field may belong to a CT component, in which case the
    // categories request must target the component_<id> table and not "content". The ID of the table's record
    // is assigned by FormControlContentContent to the field's contentComponentsItemId property.
    if (tableName.value?.indexOf('component_') === 0) {
        currentId = props.field.contentComponentsItemId || currentId
    }

    return currentId
})

const formSmall = computed(() => {
    return formStore.value?.formSmall
})

const categoryIds = computed(() => {
    return props.currentData?.[props.field.name]
})

// Methods
const selectCategories = () => {
    modalsStore.show({
        componentName: 'SelectListModal',
        obj: {
            formId: props.formId,
            listingName: targetTableName.value + '.' + props.field.name,
            selectedIds: props.currentData[props.field.name],
            // Providing an empty onSelect function triggers the view to use onMetaClick in place of onClick.
            onSelect: ids => {
            }
        }
    })
}

const manageCategories = () => {
    router.push({name: 'list', params: {component: targetTableName.value}})
}

const editItem = (catId) => {
    router.push({name: 'form', params: {component: targetTableName.value, id: catId}})
}

const moveUp = (catId) => {
    let ids = props.currentData[props.field.name]
    let i = ids.indexOf(catId)
    ids.splice(i, 1)
    ids.splice(i - 1, 0, catId)
}

const moveDown = (catId) => {
    let ids = props.currentData[props.field.name]
    let i = ids.indexOf(catId)
    ids.splice(i, 1)
    ids.splice(i + 1, 0, catId)
}

const removeCategory = (catId) => {
    let ids = props.currentData[props.field.name]
    let i = ids.indexOf(catId)
    ids.splice(i, 1)
}

// Emulating asynchronous created hook logic
onMounted(async () => {
    const o = await categoriesStore.getTableColumnData({
        tableName: tableName.value,
        columnName: props.field.name
    })

    let ids = o?.filter(item => item.itemId === id.value)?.map(item => item.catId) || []
    ids = [...new Set(ids)]

    formStore.value?.presetData({
        name: props.field.name,
        value: ids,
    })
})
</script>

<template>
    <!-- Note: v-model here reflects the original structure, though native divs do not support v-model -->
    <div :value="categoryIds" :name="field.name">
        <div v-if="!field.readonly" class="mt-1 mb-2">
            <Button :disabled="field.disabled" @click="selectCategories" :class="{'btn-sm border': formSmall}">
                <i class="bi-plus-lg"/>
            </Button>

            <Button v-if="targetTableName" v-user-access="targetTableName" :disabled="field.disabled"
                    @click="manageCategories" :class="{'btn-sm border': formSmall}">
                <i class="bi-pencil-square"/>
            </Button>
        </div>

        <div v-if="targetTableName" v-show="!field.disabled" class="list-group mb-0">
            <!-- Fixed the Vue 2 strict mix of v-if and v-for inside a single tag by handling exclusion with filtering or block wraps if needed. Keeping inline condition syntax compatible -->
            <template v-for="(catId, index) in categoryIds">
                <a v-if="!restrictedIds || restrictedIds.indexOf(catId) === -1" :key="catId"
                   class="list-group-item d-flex align-items-center" href @click.prevent="editItem(catId)">
                    <FormControlCategoryTitle :id="catId" :table-name="targetTableName" class="flex-fill"/>

                    <ItemStatusIcons :table-name="targetTableName" :id="catId" class="me-2 gap-2"/>

                    <template v-if="!field.readonly">
                        <Button v-if="!selectLimit || selectLimit > 1" class="btn-sm" :disabled="!index"
                                @click.prevent.stop="moveUp(catId)">
                            <i class="bi-chevron-up"/>
                        </Button>

                        <Button v-if="!selectLimit || selectLimit > 1" class="btn-sm"
                                :disabled="index === categoryIds.length - 1"
                                @click.prevent.stop="moveDown(catId)">
                            <i class="bi-chevron-down"/>
                        </Button>

                        <Button @click.prevent.stop="removeCategory(catId)" class="btn-sm">
                            <i class="bi-x-lg"/>
                        </Button>
                    </template>
                </a>
            </template>
        </div>
    </div>
</template>